import { Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Disc3, Headphones, Settings2, Sliders, Volume2 } from "lucide-react";

const steps = [
  {
    n: 1,
    Icon: ExternalLink,
    title: "Install BlackHole 2ch",
    body: (
      <>
        Download the free, MIT-licensed installer from the official Existential Audio repo and run it. BlackHole creates a "virtual audio cable" your DJ software can output into and your browser can listen to.
        <div className="mt-3">
          <a
            href="https://github.com/ExistentialAudio/BlackHole/releases"
            target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-md bg-[#FF007F]/15 hover:bg-[#FF007F]/30 border border-[#FF007F]/40 text-sm font-semibold text-[#FF007F]"
            data-testid="blackhole-download-link">
            <ExternalLink className="w-4 h-4" />
            Download BlackHole on GitHub
          </a>
        </div>
      </>
    ),
  },
  {
    n: 2,
    Icon: Settings2,
    title: "Open Audio MIDI Setup",
    body: (
      <>
        Press <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/20 font-mono text-[11px]">⌘ Space</kbd> and type <span className="font-mono">Audio MIDI Setup</span>. In the bottom-left of that window, click <span className="font-semibold">+ → Create Multi-Output Device</span>. Check both your speakers/headphones AND <span className="font-mono">BlackHole 2ch</span>. This way you'll still hear yourself locally while Full Spectrum Center captures the same audio.
      </>
    ),
  },
  {
    n: 3,
    Icon: Sliders,
    title: "Point your DJ software's output at BlackHole",
    body: (
      <>
        Open Virtual DJ / Serato / Rekordbox / djay Pro and find the audio output settings.
        Set <span className="font-semibold">Master Output</span> to the Multi-Output Device you just created (or directly to <span className="font-mono">BlackHole 2ch</span> if you don't need local monitoring).
        Keep <span className="font-semibold">Headphone/Cue Output</span> on your real headphones — the audience won't hear that.
      </>
    ),
  },
  {
    n: 4,
    Icon: Volume2,
    title: "In Full Spectrum Center, hit Share Audio",
    body: (
      <>
        Click the <span className="font-semibold">Share Audio</span> toggle on the DJ console header. When Chrome's picker pops up, choose <span className="font-mono">Chrome Tab</span> or <span className="font-mono">Window</span>, but the important bit is to tick the <span className="font-semibold">"Share tab audio" / "Share system audio"</span> checkbox in the bottom-left. On macOS the system-audio source you'll pick is <span className="font-mono">BlackHole 2ch</span>.
      </>
    ),
  },
  {
    n: 5,
    Icon: Headphones,
    title: "Type your Now Playing label",
    body: (
      <>
        While Share Audio is active the engine can't read your DJ software's metadata, so use the <span className="font-semibold">Now Playing</span> field that appears in the banner to tell the room what's on. It updates the participant header in real time.
      </>
    ),
  },
];

export default function BlackholeSetup() {
  return (
    <div className="min-h-screen bg-[#050508] text-white">
      <header className="sticky top-0 z-10 backdrop-blur bg-[#050508]/80 border-b border-white/5">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="p-2 rounded-lg hover:bg-white/10 border border-white/10" data-testid="back-link">
            <ArrowLeft className="w-4 h-4" />
          </Link>
          <Disc3 className="w-5 h-5 text-[#FF007F]" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-white/40">Full Spectrum Center · Help</div>
            <h1 className="font-extrabold text-lg leading-tight">Capture macOS DJ audio with BlackHole</h1>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-8" data-testid="blackhole-setup-page">
        <p className="text-white/70 leading-relaxed mb-8">
          On macOS, Chrome's <span className="font-mono">getDisplayMedia</span> picker can only grab audio from a <em>tab</em> or — with a virtual audio cable — from your <em>whole system</em>. Five minutes of one-time setup with BlackHole and you'll be able to broadcast Virtual DJ, Serato, Rekordbox, or any other DJ app directly through Full Spectrum Center — same way Zoom's "Share computer sound" works.
        </p>

        <ol className="space-y-5">
          {steps.map(({ n, Icon, title, body }) => (
            <li key={n} className="flex gap-4 p-4 rounded-xl border border-white/10 bg-white/[0.03]"
                data-testid={`blackhole-step-${n}`}>
              <div className="shrink-0 w-10 h-10 rounded-lg grid place-items-center bg-gradient-to-br from-[#FF007F]/30 to-[#00F0FF]/30 border border-white/10">
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-[0.25em] text-white/40 mb-1">Step {n}</div>
                <h3 className="font-bold text-base mb-2">{title}</h3>
                <div className="text-sm text-white/80 leading-relaxed">{body}</div>
              </div>
            </li>
          ))}
        </ol>

        <div className="mt-8 p-4 rounded-xl border border-[#00F0FF]/20 bg-[#00F0FF]/5 text-sm">
          <div className="text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] font-bold mb-1">Why not just integrate SoundCloud / YouTube?</div>
          <p className="text-white/70">
            Licensing. We're a transport layer (like Zoom or Discord), not a music distributor. Your subscription, your software, your set — we just carry the bits. This keeps you in control and out of platform restrictions.
          </p>
        </div>

        <p className="mt-8 text-xs text-white/30 text-center">
          Screenshots for each step will land here in a future build — the procedure above already works today.
        </p>
      </main>
    </div>
  );
}
